import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeftRight } from "lucide-react";

/**
 * /compare?a=<espn_id>&b=<espn_id>
 * Side-by-side comparison of two NFL players. When no ids, the user picks from
 * the Titans roster on the left and any Titan or NFL player on the right.
 */
export default function ComparePage() {
  const [params, setParams] = useSearchParams();
  const aId = params.get("a") || "";
  const bId = params.get("b") || "";

  const [roster, setRoster] = useState([]);
  const [playerA, setPlayerA] = useState(null);
  const [playerB, setPlayerB] = useState(null);
  const [loadA, setLoadA] = useState(false);
  const [loadB, setLoadB] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster");
        setRoster(data?.athletes || []);
      } catch { setRoster([]); }
    })();
  }, []);

  useEffect(() => {
    if (!aId) { setPlayerA(null); return; }
    setLoadA(true);
    api.get(`/player/${aId}`).then((r) => setPlayerA(r.data)).catch(() => setPlayerA(null)).finally(() => setLoadA(false));
  }, [aId]);
  useEffect(() => {
    if (!bId) { setPlayerB(null); return; }
    setLoadB(true);
    api.get(`/player/${bId}`).then((r) => setPlayerB(r.data)).catch(() => setPlayerB(null)).finally(() => setLoadB(false));
  }, [bId]);

  const setSide = (side, id) => {
    const next = new URLSearchParams(params);
    if (id) next.set(side, id); else next.delete(side);
    setParams(next);
  };

  const rows = useMemo(() => {
    if (!playerA || !playerB) return [];
    // Build a union of stat names across the two players.
    const byName = {};
    (playerA.career_stats || []).forEach((s) => {
      byName[s.name] = { category: s.category, name: s.name, a: s.value, b: null };
    });
    (playerB.career_stats || []).forEach((s) => {
      if (byName[s.name]) byName[s.name].b = s.value;
      else byName[s.name] = { category: s.category, name: s.name, a: null, b: s.value };
    });
    return Object.values(byName).slice(0, 40);
  }, [playerA, playerB]);

  return (
    <div data-testid="compare-page" className="max-w-[1300px] mx-auto px-5 md:px-10 py-10">
      <div className="mb-8">
        <div className="font-cond uppercase tracking-[0.28em] text-[11px] text-titans-blue">Head to head</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title flex items-center gap-3">
          Compare Players <ArrowLeftRight className="text-titans-red" size={32} />
        </h1>
        <p className="text-titans-silver max-w-2xl font-body mt-3">
          Pick any Titan on the left and match him against another Titan (or any NFL player by ESPN athlete id) on the right.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <PlayerColumn side="a" id={aId} player={playerA} loading={loadA} roster={roster} onPick={(id) => setSide("a", id)} />
        <PlayerColumn side="b" id={bId} player={playerB} loading={loadB} roster={roster} onPick={(id) => setSide("b", id)} />
      </div>

      {playerA && playerB && (
        <div className="mt-10 titans-card overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Stat</th>
                <th className="px-4 py-3 text-right">{playerA.fullName}</th>
                <th className="px-4 py-3 text-right">{playerB.fullName}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(138,141,143,0.15)]">
              {rows.map((r, i) => {
                const aWin = numeric(r.a) > numeric(r.b);
                const bWin = numeric(r.b) > numeric(r.a);
                return (
                  <tr key={i}>
                    <td className="px-4 py-2 text-titans-blue font-cond uppercase tracking-wider text-[10px]">{r.category}</td>
                    <td className="px-4 py-2 text-white font-body">{r.name}</td>
                    <td className={`px-4 py-2 text-right font-display ${aWin ? "text-titans-red" : "text-white"}`}>{r.a ?? "—"}</td>
                    <td className={`px-4 py-2 text-right font-display ${bWin ? "text-titans-red" : "text-white"}`}>{r.b ?? "—"}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={4} className="p-4 text-titans-muted italic">No comparable stats yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function numeric(v) {
  if (v == null) return -Infinity;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return isFinite(n) ? n : -Infinity;
}

function PlayerColumn({ side, id, player, loading, roster, onPick }) {
  return (
    <div className="titans-card p-5" data-testid={`compare-column-${side}`}>
      <label className="block font-cond uppercase tracking-wider text-[10px] text-titans-silver mb-2">Player {side.toUpperCase()}</label>
      <select
        value={id || ""}
        onChange={(e) => onPick(e.target.value)}
        data-testid={`compare-select-${side}`}
        className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-2 font-body mb-4"
      >
        <option value="">— Pick a Titan —</option>
        {roster.map((r) => (<option key={r.id} value={r.id}>{r.fullName} · {r.position}</option>))}
      </select>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="…or paste ESPN athlete id"
          onKeyDown={(e) => { if (e.key === "Enter") onPick(e.currentTarget.value.trim()); }}
          data-testid={`compare-espn-${side}`}
          className="flex-1 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-3 py-2 font-body text-sm"
        />
      </div>
      {loading && <div className="mt-4 text-titans-silver">Loading…</div>}
      {player && (
        <div className="mt-5 flex items-center gap-4" data-testid={`compare-player-${side}`}>
          {player.headshot && <img src={player.headshot} alt={player.fullName} className="w-20 h-20 object-cover object-top border-2 border-titans-blue/50" loading="lazy" decoding="async" />}
          <div>
            <div className="font-display text-2xl uppercase leading-tight">{player.fullName}</div>
            <div className="font-cond uppercase tracking-wider text-xs text-titans-blue">{player.positionName || player.position} · #{player.jersey || "—"}</div>
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1">{player.team}</div>
          </div>
        </div>
      )}
    </div>
  );
}
